import {
  InputLabel,
  Select,
  IconButton,
  MenuItem,
  FormControl,
  Tooltip,
  Box,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { FC, memo, useCallback, useEffect, useState } from "react";
import NumberRangeFilter from "./number-range-filter";
import MultiSelectFilter from "./multi-select-filter";
import ListFilter from "../../types/ListFilter";

const sxStyles = {
  resetButton: {
    padding: "0",
    marginRight: "2rem",
    "&:hover": {
      backgroundColor: "transparent",
      border: "none",
    },
  },
};

interface FilterControlProps {
  filter: ListFilter;
  handleFilterChange: (any, ListFilter) => void;
}

const FilterControl: FC<FilterControlProps> = ({
  filter,
  handleFilterChange,
}) => {
  // For multi/multi_date filters
  const [multiSelectedValues, setMultiSelectedValues] = useState<string[]>(
    () => {
      if (filter.value) {
        return Array.isArray(filter.value)
          ? [...filter.value]
          : [filter.value as string];
      }
      return [];
    }
  );

  // For single-select filters
  const [selectedValue, setSelectedValue] = useState(() => {
    if (filter.value && !Array.isArray(filter.value)) {
      return filter.value as string;
    }
    return "";
  });

  const [startNum, setStartNum] = useState(null);
  const [endNum, setEndNum] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleMouseEnter = () => {
    if (selectedValue.length >= 9) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const handleSelectFilterOption = (event) => {
    const filterOptionValue = event.target.value;
    setSelectedValue(filterOptionValue);
    handleFilterChange(filterOptionValue, filter);
  };

  const handleMultiSelectChange = (values: string[]) => {
    setMultiSelectedValues(values);
    handleFilterChange(values.length > 0 ? values : "all", filter);
  };

  const handleNumRange = useCallback(
    (startNum, endNum) => {
      setStartNum(startNum);
      setEndNum(endNum);
      const newRange = !startNum && !endNum ? [] : [startNum, endNum];
      handleFilterChange(newRange, filter);
    },
    [handleFilterChange, filter]
  );

  const handleResetFilter = () => {
    setSelectedValue("all");
    handleFilterChange("all", filter);
  };

  const handleResetMultiFilter = () => {
    setMultiSelectedValues([]);
    handleFilterChange("all", filter);
  };

  const isNumberFilter = filter.filter_type === "number";
  const isMultiFilter = filter.filter_type?.startsWith("multi");

  useEffect(() => {
    if (isMultiFilter) {
      if (filter.value) {
        const values = Array.isArray(filter.value)
          ? filter.value
          : [filter.value];
        setMultiSelectedValues(values);
      } else {
        setMultiSelectedValues([]);
      }
    } else if (!isNumberFilter) {
      setSelectedValue(
        filter.value && !Array.isArray(filter.value) ? String(filter.value) : ""
      );
    }
  }, [filter.value, isMultiFilter, isNumberFilter]);

  const FilterComponent = () => {
    if (isNumberFilter) {
      return (
        <NumberRangeFilter
          label={filter.label}
          startNum={startNum}
          endNum={endNum}
          onChange={handleNumRange}
        />
      );
    }

    if (isMultiFilter) {
      return (
        <MultiSelectFilter
          label={filter.label}
          options={filter.filter_options}
          selectedValues={multiSelectedValues}
          onChange={handleMultiSelectChange}
          onReset={handleResetMultiFilter}
        />
      );
    }

    return (
      <Tooltip open={showTooltip} title={selectedValue} arrow>
        <Box
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{ display: "flex", width: "100%" }}
        >
          <InputLabel>{filter.label}</InputLabel>
          <Select
            value={selectedValue}
            sx={{ width: "100%" }}
            onChange={handleSelectFilterOption}
            endAdornment={
              selectedValue &&
              selectedValue !== "all" && (
                <IconButton
                  sx={sxStyles.resetButton}
                  onClick={handleResetFilter}
                >
                  <Close />
                </IconButton>
              )
            }
          >
            <MenuItem key="all" value={"all"}>
              All
            </MenuItem>
            {filter.filter_options.map((fo) => (
              <MenuItem key={fo} value={fo}>
                {fo}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Tooltip>
    );
  };

  return (
    <FormControl
      variant="standard"
      sx={{
        width: isNumberFilter ? "15rem" : "10rem",
        marginRight: "1.5rem",
      }}
    >
      <FilterComponent />
    </FormControl>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(FilterControl, (prevProps, nextProps) => {
  // Only re-render if filter data actually changed
  return (
    prevProps.filter.id === nextProps.filter.id &&
    prevProps.filter.value === nextProps.filter.value &&
    JSON.stringify(prevProps.filter.filter_options) ===
      JSON.stringify(nextProps.filter.filter_options)
  );
});
